import { Injectable, isDevMode } from '@angular/core';
import { Subject, Observable, EMPTY } from 'rxjs';
import { FormsTurnsDaoService } from 'src/app/dao/turns-dao-services/forms-turns-dao-service/forms-turns-dao.service';
import { UserService } from 'src/app/services/user-service/user.service';
import { FormBasic } from 'src/app/models/turns-models/turns-form-basic.model';
import { Schedule } from 'src/app/models/schedule-models/schedule.model';
import { Desk } from 'src/app/models/turns-models/desk.model';
import { FormGroup } from '@angular/forms';
import { Context } from 'src/app/models/contexts-models/context.model';
import { FormAccessListKey } from 'src/app/models/turns-models/turns-form-access-list-key.model';
import { UserStorageService } from '../../user-storage-service/user-storage.service';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';

@Injectable({
  providedIn: 'root'
})
export class FormsTurnsService {

  accessToken = 'TUR';
  refreshFormsListener = new Subject<any>();
  refreshFormListener = new Subject<any>();
  filterFormsListener = new Subject<any>();
  searchFormsListener = new Subject<any>();
  rememberFormInfoFromScheduleListListener = new Subject<any>();
  rememberedSchedules = new Array<Schedule>();
  rememberedFormForm: FormGroup;
  rememberedFormDesks = new Array<Desk>();
  toggleFormsDispositionListener = new Subject<any>();

  constructor(private formsTurnsDaoService: FormsTurnsDaoService,
              private userStorageService: UserStorageService,
              private userService: UserService) { }

  public getForms() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getForms(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const forms = new Array<FormBasic>();
          o['data']['turnFormsList'].forEach(
            (f) => {
              const form = new FormBasic();
              Object.assign(form, f);
              forms.push(form);
            }
          );
          return forms;
        })
      );
    }
    return EMPTY;
  }

  public getFormInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const form = new FormBasic();
          Object.assign(form,  o['data']['turnFormDetails']);
          const schedules = this.mapSchedules(o['data']['turnFormDetails']['calendarsBound']);
          const desks = new Array<Desk>();
          o['data']['turnFormDetails']['desksBound'].forEach(
            (d) => {
              const desk = new Desk();
              Object.assign(desk, d);
              desks.push(desk);
            }
          );
          form.CalendarsBound = schedules;
          form.AvailabilityCalendarsBound = this.mapSchedules(o['data']['turnFormDetails']['availabilityCalendarsBound']);
          form.DesksBound = desks;
          return form;
        })
      );
    }
    return EMPTY;
  }

  private mapSchedules(rawSchedules) {
    const schedules = new Array<Schedule>();
    (rawSchedules || []).forEach(
      (s) => {
        const schedule = new Schedule();
        Object.assign(schedule, s);
        schedules.push(schedule);
      }
    );
    return schedules;
  }

  public addNewForm(active, name, selectedCenter, schedules, selectedDesks, addName, addSurname, addDNI, addMail, addTelephone, addLicensePlate, addPassport, addDocument, addCard, intervalCapacity, intervalDuration, maxReservation, intervalOccupacityAlert, reservationForDay, showIntervalFormat, selectedSessionContext) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.addNewForm(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, name, selectedCenter, schedules, selectedDesks, addName, addSurname, addDNI, addMail, addTelephone, addLicensePlate, addPassport, addDocument, addCard, intervalCapacity, intervalDuration, maxReservation, intervalOccupacityAlert, reservationForDay, showIntervalFormat, selectedSessionContext).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormInfo(internalId, active, turnFormName, centerInternalId, sessionContextInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, turnFormName, centerInternalId, sessionContextInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormUserDataInfo(internalId, active, addName, addSurname, addDni, addMail, addTelephone, addPassport, addLicensePlate, addDocument, addCard) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormUserDataInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, addName, addSurname, addDni, addMail, addTelephone, addPassport, addLicensePlate, addDocument, addCard).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormAllowedTimes(internalId, allowAfter, allowBefore) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormAllowedTimes(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, allowAfter, allowBefore).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormTokenLength(internalId, tokenLength) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormTokenLength(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, tokenLength).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updatePrivateFormDaysToShow(internalId, daysToShow) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updatePrivateFormDaysToShow(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, daysToShow).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updatePublicFormDaysToShow(internalId, daysToShow) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updatePublicFormDaysToShow(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, daysToShow).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormAccessListFlags(internalId, accessListEnable, checkListUserAccount) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormAccessListFlags(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, accessListEnable, checkListUserAccount).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormStripDataInfo(internalId, active, intervalCapacity, intervalDuration, maxReservation, intervalOccupacityAlert, reservationForDay) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormStripDataInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, intervalCapacity, intervalDuration, maxReservation, intervalOccupacityAlert, reservationForDay).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateShowIntervalFormat(internalId, showIntervalFormat) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateShowIntervalFormat(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, showIntervalFormat).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormDesks(internalId, active, desksBound) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormDesks(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, desksBound).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormTurnsGeoLocation(internalId, latitude, longitude, distanceInMeters) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormTurnsGeoLocation(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, latitude, longitude, distanceInMeters).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormTurnsOperative(internalId, maxTurnsPerDayByService, showEstimatedTime) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormTurnsOperative(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, maxTurnsPerDayByService, showEstimatedTime).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getFormAccessListKeys(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormAccessListKeys(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const accessListKeys = new Array<FormAccessListKey>();
          o['data']['accessList'].forEach(
            (k) => {
              const accesListKey = new FormAccessListKey();
              Object.assign(accesListKey, k);
              accessListKeys.push(accesListKey);
            }
          );
          return accessListKeys;
        })
      );
    }
    return EMPTY;
  }

  public addNewFormAccessValue(turnFormInternalId, accessValue) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.addNewFormAccessValue(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), turnFormInternalId, accessValue).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteNewFormAccessValue(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.deleteNewFormAccessValue(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateStatusForm(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateStatusForm(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['status'])
      );
    }
    return EMPTY;
  }

  public updateShowOnlineForm(internalId, online) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateShowOnlineForm(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, online).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteForm(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.deleteForm(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['success'])
      );
    }
    return EMPTY;
  }

  public linkExistingFormSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.linkExistingFormSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getFormExistingSchedules(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormExistingSchedules(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedules = new Array<Schedule>();
          o['data']['calendarList'].forEach(
            (ct) => {
              const schedule = new Schedule();
              Object.assign(schedule, ct);
              schedules.push(schedule);
            }
          );
          return schedules;
        })
      );
    }
    return EMPTY;
  }

  public deleteFormSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.deleteFormSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormSchedules(internalId, active, schedules) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormSchedules(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, schedules).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormSchedule(internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public linkExistingFormAvailabilitySchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.linkExistingFormAvailabilitySchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteFormAvailabilitySchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.deleteFormAvailabilitySchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateFormAvailabilitySchedule(internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.updateFormAvailabilitySchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addNewFormAvailabilitySchedule(relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.addNewFormAvailabilitySchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getFormAvailabilityScheduleInfo(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormAvailabilityScheduleInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addNewFormSchedule(relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.addNewFormSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getFormDesks(centerInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormDesks(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const desks = new Array<Desk>();
          o['data']['deskList'].forEach(
            (d) => {
              const desk = new Desk();
              Object.assign(desk, d);
              desks.push(desk);
            }
          );
          return desks;
        })
      );
    }
    return EMPTY;
  }

  public getFormScheduleInfo(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormScheduleInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getFormSessionContexts(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.formsTurnsDaoService.getFormSessionContexts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const contexts = new Array<Context>();
            o['data']['sessionsList'].forEach(
              (msc) => {
                const msessionContext = new Context();
                Object.assign(msessionContext, msc);
                contexts.push(msessionContext);
              }
            );
          return contexts;
        })
      );
    }
    return EMPTY;
  }

  public refreshFormsListen() {
    return this.refreshFormsListener.asObservable();
  }

  public refreshForms() {
    this.refreshFormsListener.next();
  }

  public refreshFormListen() {
    return this.refreshFormListener.asObservable();
  }

  public refreshForm() {
    this.refreshFormListener.next();
  }

  public filterForms(opts) {
    this.filterFormsListener.next(opts);
  }

  public filterFormsListen() {
    return this.filterFormsListener.asObservable();
  }

  public searchForms(filterKey) {
    this.searchFormsListener.next(filterKey);
  }

  public searchFormsListen() {
    return this.searchFormsListener.asObservable();
  }

  public rememberFormInfoFromSchedule() {
    this.rememberFormInfoFromScheduleListListener.next();
  }

  public rememberFormInfoFromScheduleListen() {
    return this.rememberFormInfoFromScheduleListListener.asObservable();
  }

  public getRememberedSchedules() {
    return this.rememberedSchedules;
  }

  public getRememberedFormInfo() {
    return { rememberedFormForm: this.rememberedFormForm, rememberedFormDesks: this.rememberedFormDesks };
  }

  public toRememberedSchedules(schedule) {
    this.rememberedSchedules.push(schedule);
  }

  public deleteRememberedSchedule(schedule) {
    this.rememberedSchedules.splice(
      this.rememberedSchedules.findIndex(
        (s) => {
          return schedule.CommentNote === s.CommentNote;
        }
      ), 1);
  }

  public deleteAllRememberedSchedule() {
    this.rememberedSchedules = new Array<Schedule>();
  }

  public rememberFormInfo(formForm, formDesks) {
    this.rememberedFormForm = formForm;
    this.rememberedFormDesks = formDesks;
  }

  public toggleFormsDisposition(viewDis) {
    this.toggleFormsDispositionListener.next(viewDis);
  }

  public toggleFormsDispositionListen() {
    return this.toggleFormsDispositionListener.asObservable();
  }
}
